import { SmartLink } from '../smart-link/SmartLink';
import IconUI from '../icon/IconUI';

// Lighthouse's SEO `link-text` audit fails any link whose rendered text is a
// generic phrase. It reads `node.innerText` and ignores `title` and
// `aria-label` entirely, so the only way to pass is to lengthen the link's own
// text — but the visible button copy is a design decision, and it's authored in
// WordPress, so we don't want to touch it.
//
// CSS-clipped text still counts as rendered. A `.visuallyHidden` prefix (see
// assets/styles/global/_base.scss) therefore extends both the accessible name
// and the audit's view of the link while changing nothing on screen. Keyed by
// destination, because the whole point is to say where the link goes.
const LINK_TEXT_CONTEXT = {
  '/learn-more': 'Inquire here to ', // -> "Inquire here to Learn More"
};

// Mirrors Lighthouse 13's `nonDescriptiveLinkTexts.en`
// (core/audits/seo/link-text.js). Kept deliberately in sync: the prefix should
// only appear while the CMS label is one the audit would actually reject, so
// the moment an editor writes a descriptive label the prefix disappears on its
// own rather than sitting here contradicting the CMS.
const NON_DESCRIPTIVE_LABELS = new Set([
  'click here',
  'click this',
  'go',
  'here',
  'information',
  'learn more',
  'more',
  'more info',
  'more information',
  'right here',
  'read more',
  'see more',
  'start',
  'this',
]);

export default function Cta({
  cta,
  className = '',
  size = 'regular',
  theme = 'primary',
  icon = 'chevronRight',
}) {
  if (!cta || !cta.url || cta.type === 'none') {
    return null;
  }

  const url = cta.url;
  const isDocument = cta.type === 'document';
  const noLabelClass = !cta.buttonLabel ? 'button--noLabel' : '';

  const needsContext = NON_DESCRIPTIVE_LABELS.has(
    (cta.buttonLabel || '').trim().toLowerCase()
  );
  const hiddenPrefix = needsContext ? LINK_TEXT_CONTEXT[url] || '' : '';

  const buttonContent = (
    <div className="button-inner">
      {cta.buttonLabel && (
        <div className="button-text">
          {hiddenPrefix && (
            <span className="visuallyHidden">{hiddenPrefix}</span>
          )}
          <span>{cta.buttonLabel}</span>
        </div>
      )}
      {icon && (
        <>
          <IconUI icon={icon} />
          {theme !== 'tertiary' && <IconUI icon={icon} />}
        </>
      )}
    </div>
  );

  const buttonClasses = `button button--${theme} ${className} ${noLabelClass}`;

  if (isDocument) {
    return (
      <a
        className={buttonClasses}
        href={url}
        title={cta.buttonLabel}
        target="_blank"
        rel="noopener noreferrer"
        data-size={size}
      >
        {buttonContent}
      </a>
    );
  }

  return (
    <SmartLink
      href={url}
      className={buttonClasses}
      title={cta.buttonLabel}
      data-size={size}
    >
      {buttonContent}
    </SmartLink>
  );
}
